import { hexToLinear, type LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { Vec3 } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { PATTERN } from '../../../render/patterns/patternIds';
import { beamSolid, boxSolid, orientedFace, planPoint, prismSolid } from '../solids';
import { gableRoof } from '../roofSolids';
import { readNumber } from './params';
import {
  dominantAxis,
  meshFrameOf,
  planBounds,
  planFrameOf,
  toLayout,
  type PlanBounds,
  type PlanFrame,
} from './planFrame';

const DEG = Math.PI / 180;

/**
 * This builder represents an open rope-working lane. It does not classify every long industrial
 * outline as a lane: the roofed Lastage ranges use buildLongIndustrialShed below. The strands ran
 * down the lane on a row of trestles, and only the head of the walk carried a roof: the baanschuur,
 * where the hemp, the yarn, the blocks and the hackles were kept and where the great wheel stood that
 * twisted the yarn out of the hackles. The walks lie at the edge of the town or just outside it —
 * round the Singel early in the century, in the Lastage by 1538 — and the layout traces them as long
 * strips of ground. Dimensions and equipment are reconstruction estimates.
 */
export const ROPE_WALK = {
  /* The baan: trodden earth, levelled and lifted this far over the field. The lift keeps the lane out
     of the ground's own plane, which the painter view at 3 km resolves to about 3 cm, and reads as
     the painter's pale strip; it is low enough to walk over without a step (PLAN §7). */
  lane: { topY: 0.15, baseY: -0.9, verge: 1.2 },
  /* The baanschuur: one short timber shed at the head of the walk, as wide as the land the layout
     traces and 16 m long against walks of 170-300 m. De Fremery gives the walks' lengths, not their
     section (lastage.md); the eave is the walk's own `height` and the pitch the surrounding
     landmarks' 50 degrees. */
  shed: {
    length: 16,
    fallbackEaveY: 2.4,
    pitch: 50 * DEG,
    door: { width: 2.4, height: 2.1, thickness: 0.08 },
  },
  /* The trestles (schragen) the strands rest on, in one row down the middle of the lane: two legs, a
     crossbar and three pins. Their spacing is a working value, not a traced one; 4 m keeps the row
     reading as a dotted line from the painter's height without a forest of geometry. */
  trestle: {
    spacing: 4,
    height: 0.95,
    legHalfWidth: 0.06,
    barHalfLength: 0.45,
    pinHeight: 0.28,
    pinHalfWidth: 0.03,
  },
  /* The great wheel at the shed door, which the yarn ran from: a spoked disc standing across the lane
     between two cheeks, its hub at the height a man turns it from. */
  wheel: {
    radius: 1.5,
    thickness: 0.16,
    segments: 16,
    spokes: 6,
    spokeHalfWidth: 0.055,
    hubY: 1.55,
    cheekHalfWidth: 0.07,
    clearance: 2.2,
  },
} as const;

/**
 * The walks' long walls, Lab 30.1 14.7 31.9 on the painting's nearest walk (museum scan 1472-1873 x
 * 3329-3513) and on Burchman Woutersz's shed; the woodcut hatches them as boarded walls.
 */
export const PAINTED_ROPE_WALK_WALL: LinearRGB = hexToLinear('#673d14');

export interface RopeWalkColors {
  /** The baanschuur's boarded walls. */
  readonly wall: LinearRGB;
  /** Its roof. */
  readonly roof: LinearRGB;
  /** Its doorway. */
  readonly opening: LinearRGB;
  /** The baan's trodden earth. */
  readonly lane: LinearRGB;
  /** Trestles, the wheel and its cheeks. */
  readonly timber: LinearRGB;
}

interface Styles {
  wall: VertexStyle;
  readonly roof: VertexStyle;
  readonly opening: VertexStyle;
  readonly lane: VertexStyle;
  readonly timber: VertexStyle;
}

/** Both sources look at the walks from the IJ; the shed stands at the head nearer the town. */
function headIsStart(plan: PlanFrame, r: PlanBounds): boolean {
  const at = (u: number): number => {
    const p = toLayout(plan, [u, 0]);
    return Math.hypot(p[0], p[1]);
  };
  return at(r.u0) <= at(r.u1);
}

function stylesOf(colors: RopeWalkColors, ids: { edgeId: number; seed: number }): Styles {
  /* `extent` is the face's own size in metres, which the unpatterned surfaces here never read; each
     part overrides it with the size it is built at. */
  const base = { ...ids, strokeAngle: 0, extent: [1, 1] as const };
  return {
    wall: { ...base, color: colors.wall, surface: SURFACE.wood },
    roof: { ...base, color: colors.roof, surface: SURFACE.roofThatch },
    opening: { ...base, color: colors.opening, surface: SURFACE.wood },
    lane: { ...base, color: colors.lane, surface: SURFACE.ground },
    timber: { ...base, color: colors.timber, surface: SURFACE.wood },
  };
}

/**
 * The baanschuur: boarded walls under a gable roof at the head of the walk, with its doorway as a
 * closed slab standing just proud of the lane-facing gable (the audit wants closed parts, and a
 * recessed panel would lie in the wall's own plane).
 */
function buildShed(
  builder: MeshBuilder,
  styles: Styles,
  frame: Frame,
  r: PlanBounds,
  head: boolean,
  eaveY: number,
  run = Math.min(ROPE_WALK.shed.length, (r.u1 - r.u0) * 0.5)
): void {
  const { pitch, door } = ROPE_WALK.shed;
  const u0 = head ? r.u0 : r.u1 - run;
  const u1 = u0 + run;
  const halfWidth = (r.v1 - r.v0) / 2;
  const ridgeY = eaveY + halfWidth * Math.tan(pitch);
  const wall: VertexStyle = { ...styles.wall, extent: [run, eaveY] };
  const roof: VertexStyle = { ...styles.roof, extent: [run, halfWidth / Math.cos(pitch)] };
  prismSolid(
    builder,
    wall,
    frame,
    [
      [u0, r.v0],
      [u1, r.v0],
      [u1, r.v1],
      [u0, r.v1],
    ],
    0,
    eaveY,
    { top: false, bottom: false }
  );
  gableRoof(builder, { roof, wall }, frame, {
    u0,
    u1,
    v0: r.v0,
    v1: r.v1,
    eaveY,
    ridgeY,
    ends: { start: true, end: true },
  });
  const face = head ? u1 : u0;
  const vc = (r.v0 + r.v1) / 2;
  const sill = 0.05;
  const slab = door.thickness;
  const min: Vec3 = [head ? face : face - slab, sill, -(vc + door.width / 2)];
  const max: Vec3 = [head ? face + slab : face, sill + door.height, -(vc - door.width / 2)];
  const at: VertexStyle = { ...styles.opening, extent: [door.width, door.height] };
  boxSolid(builder, at, frame, min, max, { top: true, bottom: true });
}

/** Roofed ranges distinguished from the open working lanes in both supplied Anthonisz images.
 * Bay divisions, joinery and the small roof projections are fitted interpretations. */
export function buildLongIndustrialShed(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: RopeWalkColors,
  ids: { readonly edgeId: number; readonly seed: number },
  warn: (msg: string) => void
): void {
  const plan = planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
  const frame = meshFrameOf(plan),
    r = planBounds(plan);
  const eave = readNumber(landmark.params, 'height', ROPE_WALK.shed.fallbackEaveY, warn);
  const styles = stylesOf(colors, ids),
    length = r.u1 - r.u0;
  styles.wall = { ...styles.wall, pattern: PATTERN.timberFront1538 };
  buildShed(builder, styles, frame, r, true, eave, length);
  const bays = Math.max(1, Math.round(length / 25));
  for (const v of [r.v0, r.v1]) {
    for (const y of [0.15, eave - 0.12])
      beamSolid(builder, styles.timber, frame, planPoint(r.u0, y, v), planPoint(r.u1, y, v), 0.08);
    for (let i = 0; i <= bays; i++) {
      const u = r.u0 + (length * i) / bays;
      beamSolid(builder, styles.timber, frame, planPoint(u, 0.15, v), planPoint(u, eave, v), 0.08);
      if (i < bays)
        beamSolid(
          builder,
          styles.timber,
          frame,
          planPoint(u + 0.15, 0.3, v),
          planPoint(u + 2.1, eave - 0.2, v),
          0.05
        );
    }
  }
  const half = (r.v1 - r.v0) / 2,
    vc = (r.v0 + r.v1) / 2;
  const v0 = vc - half * 0.55,
    v1 = vc - half * 0.15;
  const eaveY = eave + (half - Math.abs(v0 - vc)) * Math.tan(ROPE_WALK.shed.pitch) + 0.7;
  for (let i = 0; i < bays; i++) {
    const u = r.u0 + (length * (i + 0.5)) / bays;
    prismSolid(
      builder,
      styles.wall,
      frame,
      [
        [u - 0.75, v0],
        [u + 0.75, v0],
        [u + 0.75, v1],
        [u - 0.75, v1],
      ],
      eaveY - 0.9,
      eaveY,
      { top: false, bottom: true }
    );
    gableRoof(builder, { wall: styles.wall, roof: styles.roof }, frame, {
      u0: u - 0.75,
      u1: u + 0.75,
      v0,
      v1,
      eaveY,
      ridgeY: eaveY + 0.6,
      ends: { start: true, end: true },
    });
  }
}

/** The great wheel: a spoked disc across the lane, on two cheeks that carry its axle. */
function buildWheel(
  builder: MeshBuilder,
  styles: Styles,
  frame: Frame,
  u: number,
  vc: number
): void {
  const { radius, thickness, segments, spokes, spokeHalfWidth, hubY, cheekHalfWidth } =
    ROPE_WALK.wheel;
  const half = thickness / 2;
  const rim = (v: number, k: number): Vec3 => {
    const a = (k / segments) * Math.PI * 2;
    return planPoint(u + Math.cos(a) * radius, hubY + Math.sin(a) * radius, v);
  };
  const disc: VertexStyle = { ...styles.timber, extent: [2 * radius, 2 * radius] };
  /* Local z is -v, so the cap on the -v side of the disc faces +z. */
  orientedFace(
    builder,
    disc,
    frame,
    Array.from({ length: segments }, (_, k) => rim(vc - half, k)),
    [0, 0, 1]
  );
  orientedFace(
    builder,
    disc,
    frame,
    Array.from({ length: segments }, (_, k) => rim(vc + half, k)),
    [0, 0, -1]
  );
  for (let k = 0; k < segments; k++) {
    const a = rim(vc - half, k);
    const b = rim(vc - half, k + 1);
    const c = rim(vc + half, k + 1);
    const d = rim(vc + half, k);
    const mid: Vec3 = [(a[0] + c[0]) / 2, (a[1] + c[1]) / 2, 0];
    orientedFace(builder, disc, frame, [a, b, c, d], [mid[0] - u, mid[1] - hubY, 0]);
  }
  for (let k = 0; k < spokes; k++) {
    const a = (k / spokes) * Math.PI;
    beamSolid(
      builder,
      styles.timber,
      frame,
      planPoint(u, hubY, vc),
      planPoint(u + Math.cos(a) * radius, hubY + Math.sin(a) * radius, vc),
      spokeHalfWidth
    );
  }
  for (const side of [-1, 1]) {
    beamSolid(
      builder,
      styles.timber,
      frame,
      planPoint(u, 0, vc + side * (half + cheekHalfWidth)),
      planPoint(u, hubY + 0.1, vc + side * (half + cheekHalfWidth)),
      cheekHalfWidth
    );
  }
}

/** One row of trestles down the middle of the baan, from the shed end to the far end. */
function buildTrestles(
  builder: MeshBuilder,
  styles: Styles,
  frame: Frame,
  u0: number,
  u1: number,
  vc: number
): void {
  const { spacing, height, legHalfWidth, barHalfLength, pinHeight, pinHalfWidth } =
    ROPE_WALK.trestle;
  const room = u1 - u0;
  if (room < spacing) return;
  const count = Math.floor(room / spacing);
  const first = u0 + (room - (count - 1) * spacing) / 2;
  for (let i = 0; i < count; i++) {
    const u = first + i * spacing;
    for (const side of [-1, 1]) {
      beamSolid(
        builder,
        styles.timber,
        frame,
        planPoint(u, 0, vc + side * barHalfLength),
        planPoint(u, height, vc + side * barHalfLength),
        legHalfWidth
      );
    }
    beamSolid(
      builder,
      styles.timber,
      frame,
      planPoint(u, height, vc - barHalfLength),
      planPoint(u, height, vc + barHalfLength),
      legHalfWidth
    );
    for (const side of [-1, 0, 1]) {
      beamSolid(
        builder,
        styles.timber,
        frame,
        planPoint(u, height, vc + side * barHalfLength * 0.55),
        planPoint(u, height + pinHeight, vc + side * barHalfLength * 0.55),
        pinHalfWidth
      );
    }
  }
}

/**
 * One rope walk on the land the layout traces: the baanschuur at the head, the baan running the rest
 * of the strip as trodden earth in the open, the great wheel at the door and the trestles down the
 * lane. A walk too short for a trestle gets the shed and the wheel only.
 */
export function buildRopeWalk(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: RopeWalkColors,
  ids: { readonly edgeId: number; readonly seed: number },
  warn: (msg: string) => void
): void {
  const plan = planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
  const frame = meshFrameOf(plan);
  const r = planBounds(plan);
  const eaveY = readNumber(landmark.params, 'height', ROPE_WALK.shed.fallbackEaveY, warn);
  const styles = stylesOf(colors, ids);
  const vc = (r.v0 + r.v1) / 2;
  const halfLane = Math.max(1.2, (r.v1 - r.v0) / 2 - ROPE_WALK.lane.verge);

  /* The baan: a closed slab of trodden earth, lifted over the field and sunk below it. */
  prismSolid(
    builder,
    { ...styles.lane, extent: [r.u1 - r.u0, 2 * halfLane] },
    frame,
    [
      [r.u0, vc - halfLane],
      [r.u1, vc - halfLane],
      [r.u1, vc + halfLane],
      [r.u0, vc + halfLane],
    ],
    ROPE_WALK.lane.baseY,
    ROPE_WALK.lane.topY,
    { top: true, bottom: true }
  );

  const head = headIsStart(plan, r);
  buildShed(builder, styles, frame, r, head, eaveY);
  const shedRun = Math.min(ROPE_WALK.shed.length, (r.u1 - r.u0) * 0.5);
  const door = head ? r.u0 + shedRun : r.u1 - shedRun;
  const along = head ? 1 : -1;
  buildWheel(builder, styles, frame, door + along * ROPE_WALK.wheel.clearance, vc);
  const laneStart = door + along * (ROPE_WALK.wheel.clearance + 2.3);
  const laneEnd = head ? r.u1 : r.u0;
  buildTrestles(
    builder,
    styles,
    frame,
    Math.min(laneStart, laneEnd),
    Math.max(laneStart, laneEnd),
    vc
  );
}
